import { useState } from "react";
import { importAmazonProduct } from "../../api/client";
import type { AmzAttributes, AmzOverviewResponse, AmzProductRow } from "../../types/amazon";
import { fmtJst, formatYen, needsReviewRow } from "../../lib/amazonContent";
import { Badge, Spinner, Thumb } from "./AmzUi";
import { errorMessage, issueTone, pageStatus } from "./helpers";
import type { DetailTab, ProductFilter } from "./helpers";

interface ProductsViewProps {
  overview: AmzOverviewResponse;
  q: string;
  filter: ProductFilter;
  onFilter: (f: ProductFilter) => void;
  onOpen: (sku: string, tab: DetailTab) => void;
  onSyncAll: () => void;
  syncing: boolean;
  onImported: (sku: string) => void;
  showToast: (msg: string) => void;
}

const FILTERS: { key: ProductFilter; label: string; fn: (r: AmzProductRow) => boolean }[] = [
  { key: "all", label: "すべて", fn: () => true },
  { key: "review", label: "要確認", fn: needsReviewRow },
  { key: "error", label: "エラー", fn: (r) => r.issues.error > 0 || !!r.syncError },
  { key: "unregistered", label: "未登録", fn: (r) => !r.registered },
  { key: "updated", label: "更新あり", fn: (r) => r.registered && r.draftChanges > 0 },
];

function matchQuery(r: AmzProductRow, q: string): boolean {
  const t = q.trim().toLowerCase();
  if (!t) return true;
  return [r.itemName, r.sku, r.asin].some((x) => (x ?? "").toLowerCase().includes(t));
}

interface ImportModalProps {
  onClose: () => void;
  onDone: (sku: string) => void;
  showToast: (msg: string) => void;
}

function ImportModal({ onClose, onDone, showToast }: ImportModalProps) {
  const [sku, setSku] = useState("");
  const [productType, setProductType] = useState("FOOD_STORAGE_CONTAINER");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onFile = (file: File | undefined) => {
    if (!file) return;
    void file.text().then((t) => {
      setText(t);
      if (!sku) setSku(file.name.replace(/\.json$/i, ""));
      try {
        const o = JSON.parse(t) as { productType?: string };
        if (o.productType) setProductType(o.productType);
      } catch {
        /* 貼り付け後の検証で表示する */
      }
    });
  };

  const submit = async () => {
    setErr(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      setErr("JSONとして読み取れません。出品JSON（attributes を含むもの）をそのまま貼り付けてください。");
      return;
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      setErr("JSONのトップレベルがオブジェクトではありません。");
      return;
    }
    const o = parsed as { productType?: string; requirements?: string; attributes?: AmzAttributes };
    const attributes = o.attributes ?? (parsed as AmzAttributes);
    if (!attributes || typeof attributes !== "object" || Array.isArray(attributes) || Object.keys(attributes).length === 0) {
      setErr("attributes が見つかりません。");
      return;
    }
    const skuTrim = sku.trim();
    if (!skuTrim) {
      setErr("SKUを入力してください。");
      return;
    }
    setBusy(true);
    try {
      const r = await importAmazonProduct({
        sku: skuTrim,
        productType: o.productType || productType.trim(),
        requirements: o.requirements,
        attributes,
      });
      showToast(`${r.detail.sku} を下書きとして取り込みました。`);
      onDone(r.detail.sku);
    } catch (e) {
      setErr(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <div className="amz-overlay" role="dialog" aria-modal="true" aria-label="出品JSONを取り込む">
      <div className="amz-modal narrow">
        <div className="amz-modal-head">
          <h2>出品JSONを取り込む</h2>
        </div>
        <div className="amz-import-body">
          <span className="amz-hint">
            amazon/listings の出品JSON（productType・attributes）を、下書きとして取り込みます。Amazonへは送信されません。
          </span>
          <label>
            SKU
            <input className="amz-input" value={sku} onChange={(e) => setSku(e.target.value)} placeholder="例: STQ-G1-S" />
          </label>
          <label>
            商品タイプ（JSONに productType があればそちらを使います）
            <input className="amz-input" value={productType} onChange={(e) => setProductType(e.target.value)} />
          </label>
          <label>
            出品JSON
            <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder='{"productType":"FOOD_STORAGE_CONTAINER","attributes":{…}}' />
          </label>
          <label>
            または .json ファイルを選択
            <input type="file" accept="application/json,.json" onChange={(e) => onFile(e.target.files?.[0])} />
          </label>
          {err && <div className="amz-form-err">{err}</div>}
        </div>
        <div className="amz-modal-foot end">
          <div className="amz-modal-foot-btns">
            <button type="button" className="amz-btn" onClick={onClose} disabled={busy}>
              キャンセル
            </button>
            <button type="button" className="amz-btn primary" onClick={() => void submit()} disabled={busy || !text.trim()}>
              {busy ? "取り込み中…" : "下書きとして取り込む"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProductsView({ overview, q, filter, onFilter, onOpen, onSyncAll, syncing, onImported, showToast }: ProductsViewProps) {
  const [importOpen, setImportOpen] = useState(false);
  const base = overview.products.filter((r) => matchQuery(r, q));
  const active = FILTERS.find((f) => f.key === filter) ?? FILTERS[0];
  const rows = base.filter(active.fn);

  return (
    <div className="amz-page tight">
      <div className="amz-head">
        <div className="amz-head-text">
          <h1 className="amz-h1">Amazon商品</h1>
          <span className="amz-subtext">{rows.length}件を表示</span>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="amz-btn" onClick={() => setImportOpen(true)}>
            出品JSONを取り込む
          </button>
          <button type="button" className="amz-btn" onClick={onSyncAll} disabled={syncing}>
            {syncing ? (
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                <Spinner size="sm" />
                取得中…
              </span>
            ) : (
              "Amazonから最新取得"
            )}
          </button>
        </div>
      </div>
      <div className="amz-chips">
        {FILTERS.map((f) => (
          <button key={f.key} type="button" className={`amz-chip ${filter === f.key ? "on" : ""}`} onClick={() => onFilter(f.key)}>
            {f.label}
            <small>{base.filter(f.fn).length}</small>
          </button>
        ))}
      </div>
      <div className="amz-table-wrap">
        <div className="amz-table">
          <div className="amz-prod-grid head">
            <span>商品</span>
            <span>SKU</span>
            <span>ASIN</span>
            <span>商品ページ</span>
            <span>Issue</span>
            <span>価格</span>
            <span>最終同期</span>
          </div>
          {rows.map((r) => {
            const st = pageStatus(r);
            const issues = r.issues.error + r.issues.warning + r.issues.info;
            const tone = issueTone(r);
            return (
              <button
                key={r.sku}
                type="button"
                className="amz-prod-grid amz-prod-row"
                onClick={() => onOpen(r.sku, r.registered ? "overview" : "page")}
              >
                <div className="amz-prod-name">
                  <Thumb url={r.mainImage} />
                  <span>{r.itemName ?? "（商品名なし）"}</span>
                </div>
                <span className="amz-cell-mono">{r.sku}</span>
                <span className="amz-cell-mono" style={{ color: "var(--amz-sub)" }}>
                  {r.asin ?? "—"}
                </span>
                <Badge tone={st.tone}>{st.label}</Badge>
                <span
                  className="amz-issue-count"
                  style={{ color: issues === 0 ? "var(--amz-faint)" : `var(--amz-${tone === "warning" ? "warn" : tone === "info" ? "info" : "err"})` }}
                >
                  {issues}
                </span>
                <span style={{ fontSize: 12 }}>{formatYen(r.price)}</span>
                <span className={`amz-sync-cell ${r.syncError ? "err" : ""}`}>{r.syncError ? "同期失敗" : fmtJst(r.lastSyncedAt)}</span>
              </button>
            );
          })}
          {rows.length === 0 && <div className="amz-empty">条件に一致する商品はありません</div>}
        </div>
      </div>
      {importOpen && (
        <ImportModal
          onClose={() => setImportOpen(false)}
          showToast={showToast}
          onDone={(sku) => {
            setImportOpen(false);
            onImported(sku);
          }}
        />
      )}
    </div>
  );
}
